import { useMemo, useState } from "react";
import type { EChartsCoreOption } from "echarts/core";
import { parseTs } from "../api/labels";
import { platformLabel as label } from "../api/platforms";
import { EChart, cssVar } from "../components/EChart";
import { Badge, EmptyState, ErrorState, PageHeader, Panel, Row, Skeleton, Stack, Tag } from "@akaike/ui";
import { toneVariant } from "../lib/badge-tone";
import { Seg, Stat } from "../components/foundation";
import { type Theme, useTheme } from "../lib/theme-store";
import { AnimatedNumber } from "../lib/motion";
import {
  useWafOverviewV1WafOverviewGet,
  useWafTrendV1WafTrendGet,
} from "../api/generated/qcsControlPlane";
import type { WafEndpointOut, WafPlatformOut, WafTrendPoint } from "../api/generated/model";

// A WAF gate fails silently: a challenge arrives as a 202 with an empty body, which every
// status ladder reads as success and every parser reads as "this brand has no SKUs". This
// screen exists to make that state visible before it is mistaken for a quiet day, so it
// leans hard on distinguishing "zero because nothing happened" from "zero because we
// couldn't look", the two are rendered differently everywhere below.

const fmtInt = (n: number) => n.toLocaleString();
const fmtMs = (m: number) => (m <= 0 ? "—" : m >= 1000 ? `${(m / 1000).toFixed(1)} s` : `${Math.round(m)} ms`);
const fmtSecs = (s: number) => (s <= 0 ? "—" : s >= 60 ? `${Math.round(s / 60)}m` : `${Math.round(s)}s`);

function ago(iso: string | null | undefined): string {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - parseTs(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// The challenge ratio is the signal, not the raw counts: mints scale with lease count, so
// a busy run and an escalation look identical until you divide. Thresholds are deliberately
// loose, a few challenges per hundred mints is a normal token TTL expiring, a third of
// them dying is the WAF no longer accepting our solves.
function ratioZone(ratio: number | null | undefined): "ok" | "warn" | "crit" {
  if (ratio == null) return "ok";
  if (ratio >= 0.3) return "crit";
  if (ratio >= 0.1) return "warn";
  return "ok";
}

const GATE_TONE = { gated: "warn", unprobed: "crit", clear: "ok" } as const;
// "unprobed" is toned louder than "gated" on purpose. A known gate is handled; an endpoint
// nobody has measured is the one that silently empties a sweep, which is exactly how
// Zepto's search gate went unnoticed.
const GATE_HELP: Record<string, string> = {
  gated: "Measured: challenges without a token. The client mints one.",
  unprobed: "Never tested. Not the same as clear: assume nothing.",
  clear: "Measured: serves without a token.",
};

function GateChip({ e }: { e: WafEndpointOut }) {
  const tone = GATE_TONE[e.gate as keyof typeof GATE_TONE] ?? "warn";
  const when = e.checked_on ? ` · ${e.checked_on}` : "";
  return (
    <span title={`${GATE_HELP[e.gate] ?? ""}${e.note ? `\n\n${e.note}` : ""}`}>
      <Badge variant={toneVariant(tone)}>
        {e.name} · {e.gate}
        {when}
      </Badge>
    </span>
  );
}

function PlatformCard({ p, eventsAvailable }: { p: WafPlatformOut; eventsAvailable: boolean }) {
  const zone = ratioZone(p.challenge_ratio);
  const silent = p.mints === 0 && p.challenges === 0 && p.failures === 0;
  // Three genuinely different states, and the whole point of the card is not to collapse
  // them: dormant (gate real, no token minted by design), unknown (we could not read the
  // events), and silent (we read them, and nothing has happened).
  const status = !p.mints_tokens
    ? { tone: "brand" as const, text: "dormant" }
    : !eventsAvailable
      ? { tone: "warn" as const, text: "events unavailable" }
      : silent
        ? { tone: "warn" as const, text: "no activity" }
        : {
            tone: zone,
            text: zone === "crit" ? "escalating" : zone === "warn" ? "challenged" : "healthy",
          };

  return (
    <Panel alarm={zone === "crit" && p.mints_tokens && eventsAvailable}>
      <Panel.Head>
        <span className={`plat ${p.platform}`}>{label(p.platform)}</span>
        <code className="faint" style={{ fontSize: 10.5 }}>
          {p.domain}
        </code>
        <div className="flex-1" />
        <Badge variant={toneVariant(status.tone)}>{status.text}</Badge>
      </Panel.Head>
      <Panel.Body className="flex flex-col" style={{ gap: 14 }}>
        <Row gap="lg" className="wrap">
          <Stat
            label="challenge ratio"
            // Untinted when undefined. A green em-dash renders as a filled healthy bar,
            // which is the exact misreading this screen exists to prevent.
            tone={p.challenge_ratio == null ? undefined : zone}
            value={
              p.challenge_ratio == null ? (
                "—"
              ) : (
                <AnimatedNumber value={p.challenge_ratio} format={(n) => `${(n * 100).toFixed(1)}%`} />
              )
            }
            // Undefined, not zero. Zero would read as "no challenges, all good" on a
            // platform where nothing was ever minted.
            foot={p.challenge_ratio == null ? "no mints in window" : `${fmtInt(p.challenges)} of ${fmtInt(p.mints)} mints`}
          />
          <Stat label="mints" value={fmtInt(p.mints)} foot={`last ${ago(p.last_mint_at)}`} />
          <Stat
            label="challenges"
            value={fmtInt(p.challenges)}
            tone={p.challenges > 0 ? "warn" : undefined}
            foot={`last ${ago(p.last_challenge_at)}`}
          />
          <Stat
            label="failed solves"
            value={fmtInt(p.failures)}
            tone={p.failures > 0 ? "crit" : undefined}
            foot={p.last_error ? p.last_error.slice(0, 40) : "—"}
          />
        </Row>
        <Row gap="lg" className="wrap">
          <Stat label="solve p95" value={fmtMs(p.p95_solve_ms)} foot={`avg ${fmtMs(p.avg_solve_ms)}`} />
          <Stat label="token life" value={fmtSecs(p.avg_token_life_s)} foot="avg age at challenge" />
          <Stat
            label="exits"
            value={fmtInt(p.exits)}
            // Concentration is the tell: challenges on a few exits is IP reputation and
            // wants rotation; spread across most of them is escalation and does not.
            foot={p.exits > 0 ? `${fmtInt(p.challenged_exits)} challenged` : "—"}
          />
        </Row>
        <div className="col" style={{ gap: 6 }}>
          <span className="eyebrow">endpoints</span>
          <Row gap="sm" className="wrap">
            {p.endpoints.map((e) => (
              <GateChip key={e.name} e={e} />
            ))}
          </Row>
        </div>
        {!p.mints_tokens && (
          <p className="faint" style={{ fontSize: 11, margin: 0 }}>
            The gate is real and detected, but the live path mints no token, this platform
            uses a token-free host. The solver is kept intact for re-enable.
          </p>
        )}
      </Panel.Body>
    </Panel>
  );
}

function trendOption(rows: WafTrendPoint[], platforms: string[], _theme: Theme): EChartsCoreOption {
  const ink2 = cssVar("--ink-2", "#444");
  const faint = cssVar("--ink-3", "#888");
  const line = cssVar("--line", "#e5e5e5");
  const bg = cssVar("--panel", "#fff");
  const mono = cssVar("--f-mono", "ui-monospace, monospace");
  const crit = cssVar("--crit", "#d83a3a");
  const ok = cssVar("--ok", "#1a7f37");
  const warn = cssVar("--warn", "#d77a14");

  const buckets = [...new Set(rows.map((r) => r.bucket))].sort();
  const at = (b: string, p: string) => rows.find((r) => r.bucket === b && r.platform === p);
  // Summed across platforms: the per-platform split lives in the cards above, and three
  // series per platform would be unreadable at four platforms.
  const sum = (b: string, key: "mints" | "challenges" | "failures") =>
    platforms.reduce((n, p) => n + (at(b, p)?.[key] ?? 0), 0);

  const series = (
    [
      ["mints", ok],
      ["challenges", crit],
      ["failures", warn],
    ] as const
  ).map(([key, color]) => ({
    name: key,
    type: "line" as const,
    data: buckets.map((b) => sum(b, key)),
    smooth: true,
    showSymbol: false,
    lineStyle: { color, width: 2 },
    itemStyle: { color },
  }));

  return {
    grid: { left: 8, right: 12, top: 24, bottom: 6, containLabel: true },
    legend: {
      top: 0,
      right: 0,
      itemWidth: 10,
      itemHeight: 2,
      textStyle: { color: faint, fontSize: 10, fontFamily: mono },
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: bg,
      borderColor: line,
      borderWidth: 1,
      padding: [6, 9],
      textStyle: { color: ink2, fontSize: 11, fontFamily: mono },
    },
    xAxis: {
      type: "category",
      data: buckets.map((b) => b.slice(11, 16)),
      boundaryGap: false,
      axisLine: { lineStyle: { color: line } },
      axisTick: { show: false },
      axisLabel: { color: faint, fontSize: 9, fontFamily: mono },
    },
    yAxis: {
      type: "value",
      scale: true,
      splitNumber: 3,
      minInterval: 1,
      axisLabel: { color: faint, fontSize: 9, fontFamily: mono },
      splitLine: { lineStyle: { color: line, type: "dashed" } },
    },
    series,
  };
}

const WINDOWS = [
  { value: "6", label: "6h" },
  { value: "24", label: "24h" },
  { value: "168", label: "7d" },
];

export function Waf() {
  const [hours, setHours] = useState("24");
  const h = Number(hours);
  const { theme } = useTheme();
  const overview = useWafOverviewV1WafOverviewGet({ hours: h });
  // Bucket width tracks the window so the chart keeps ~50 points at any zoom.
  const bucketMinutes = h <= 6 ? 5 : h <= 24 ? 30 : 180;
  const trend = useWafTrendV1WafTrendGet({
    hours: h,
    bucket_minutes: bucketMinutes,
  });

  // Memoized because the option below depends on it: a fresh [] fallback on every render
  // would rebuild the ECharts option (and so the chart) on every render.
  const platforms = useMemo(() => overview.data?.platforms ?? [], [overview.data]);
  const live = overview.data?.live ?? [];
  const eventsAvailable = overview.data?.events_available ?? false;
  const trendRows = useMemo(() => trend.data ?? [], [trend.data]);
  const option = useMemo(
    () => trendOption(trendRows, platforms.map((p) => p.platform), theme),
    [trendRows, platforms, theme],
  );

  if (overview.isError)
    return (
      <ErrorState
        title="Could not load WAF status"
        message={String(overview.error)}
        onRetry={() => void overview.refetch()}
      />
    );

  return (
    <Stack gap="lg">
      <PageHeader
        title="WAF"
        description="AWS-WAF gates → token mints → challenges"
        action={<Seg value={hours} onChange={setHours} options={WINDOWS} />}
      />

      {!eventsAvailable && !overview.isLoading && (
        <Panel>
          <Panel.Body>
            <Row gap="sm">
              <Badge variant="outline-warning">events unavailable</Badge>
              <span className="faint" style={{ fontSize: 11.5 }}>
                ClickHouse is unconfigured or unreachable, so every count below is zero
                because nothing could be read, not because nothing happened. The gate map
                is still accurate: it comes from the platform registry.
              </span>
            </Row>
          </Panel.Body>
        </Panel>
      )}

      {overview.isLoading ? (
        <Skeleton h={220} />
      ) : platforms.length === 0 ? (
        <EmptyState title="No WAF-gated platforms">
          No registered platform declares a WAF profile.
        </EmptyState>
      ) : (
        <div className="grid-cards">
          {platforms.map((p) => (
            <PlatformCard key={p.platform} p={p} eventsAvailable={eventsAvailable} />
          ))}
        </div>
      )}

      <Panel>
        <Panel.Head>
          <span>Token lifecycle</span>
          <span className="faint" style={{ fontSize: 10.5 }}>
            challenges climbing toward mints = escalation
          </span>
        </Panel.Head>
        <Panel.Body>
          {trend.isLoading ? (
            <Skeleton h={150} />
          ) : trendRows.length === 0 ? (
            <EmptyState title="No WAF events in this window">
              Nothing has minted or been challenged. A gated platform with no events is not
              necessarily healthy: it may simply not have run.
            </EmptyState>
          ) : (
            <EChart option={option} height={190} />
          )}
        </Panel.Body>
      </Panel>

      <Panel>
        <Panel.Head>
          <span>Live token state</span>
          <span className="faint" style={{ fontSize: 10.5 }}>
            runs with WAF activity in the last 15 minutes
          </span>
        </Panel.Head>
        <Panel.Body>
          {live.length === 0 ? (
            <EmptyState title="No active WAF traffic">
              A token lives inside an engine subprocess and dies with its lease, so this
              shows recent mint activity per run rather than a token registry.
            </EmptyState>
          ) : (
            <Stack gap="sm">
              {live.map((l) => (
                <Row key={`${l.platform}:${l.run_id}`} gap="md">
                  <span className={`plat ${l.platform}`}>{label(l.platform)}</span>
                  <code className="faint" style={{ fontSize: 10.5 }}>
                    {l.run_id.slice(0, 8)}
                  </code>
                  <div className="flex-1" />
                  <Tag>{fmtInt(l.mints)} mints</Tag>
                  {l.challenges > 0 && <Badge variant="outline-warning">{fmtInt(l.challenges)} challenged</Badge>}
                  {l.failures > 0 && <Badge variant="outline-destructive">{fmtInt(l.failures)} failed</Badge>}
                  <span className="faint" style={{ fontSize: 10.5 }}>
                    {l.seconds_since_mint == null
                      ? "no mint yet"
                      : `minted ${fmtSecs(l.seconds_since_mint)} ago`}
                  </span>
                </Row>
              ))}
            </Stack>
          )}
        </Panel.Body>
      </Panel>
    </Stack>
  );
}
